import './App.css';

import React, { useState } from 'react';
import { Route, BrowserRouter as Router, Routes } from 'react-router-dom';

import Overview from './components/Overview';
import QuestionForm from './components/QuestionForm';
import Review from './components/Review';
import TestPage from './components/TestPage'; // Import the new TestPage component
import ThankYou from './components/ThankYou';

function InviteCodeInput({ onSubmit }) {
  const [inviteCode, setInviteCode] = useState('');
  const [error, setError] = useState('');
  const [isChecking, setIsChecking] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsChecking(true);

    try {
      const apiBase = process.env.REACT_APP_API_BASE_URL || '';
      const response = await fetch(`${apiBase}/api/invite/validate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ inviteCode }),
      });
      const result = await response.json();

      if (!response.ok || !result.valid) {
        setError(result.message || 'Invalid invite code. Please try again.');
        return;
      }

      onSubmit(inviteCode.toUpperCase());
    } catch (_) {
      setError('Unable to validate the invite code. Please try again.');
    } finally {
      setIsChecking(false);
    }
  };

  const handleInviteCodeChange = (e) => {
    setInviteCode(e.target.value.toUpperCase());
  };

  return (
    <form onSubmit={handleSubmit} className="invite-code-form">
      <input
        type="text"
        value={inviteCode}
        onChange={handleInviteCodeChange}
        placeholder="Enter invite code"
        required
      />
      <button type="submit" disabled={isChecking}>{isChecking ? 'Checking…' : 'Submit'}</button>
      {error && <p className="error-message">{error}</p>}
    </form>
  );
}

function App() {
  const [isInviteCodeValid, setIsInviteCodeValid] = useState(false);
  const [inviteCode, setInviteCode] = useState('');
  const [currentStep, setCurrentStep] = useState(0);
  // Note: attachments state seems unused here, TestPage manages its own? Keeping for now.
  const [attachments, setAttachments] = useState([]); 
  const [formData, setFormData] = useState({
    companyName: '',
    contactName: '',
    contactEmail: '',
    answers: [
      { text: '', attachments: [] },
      { text: '', attachments: [] },
      { text: '', attachments: [] },
      { text: '', attachments: [] },
      { text: '', attachments: [] }
    ]
  });

const questions = [
  "Outline the product/engineering team size, structure, and key leadership members. How is AI/ML expertise represented within the team, if at all?",
  "What is the technology stack from back end to front end? Please include any AI/ML components, frameworks, or infrastructure used.",
  "How does the technology or intellectual property contribute to the company's moat / competitive advantage? If AI is part of the solution, how does it enhance defensibility or differentiation?",
  "How do you incorporate cybersecurity and privacy in your solution? Please include how AI systems (if any) are secured, governed, and protected against misuse or bias.",
  "What are the plans for the future architecture and team over the next 12 months? Include any planned investments in AI capabilities, infrastructure, or talent."
];

  const handleInviteCodeSubmit = (code) => {
    setIsInviteCodeValid(true);
    setInviteCode(code);
    setCurrentStep(0);
  };

  const updateFormData = (field, value, index = null) => {
    if (index !== null) {
      // Update a specific answer
      setFormData(prev => {
        const newAnswers = [...prev.answers];
        // Ensure the answer object exists before updating
        if (!newAnswers[index]) {
          newAnswers[index] = { text: '', attachments: [] };
        }
        if (field === 'text') {
          newAnswers[index] = { ...newAnswers[index], text: value };
        } else if (field === 'attachments') {
          newAnswers[index] = { ...newAnswers[index], attachments: value };
        }
        return { ...prev, answers: newAnswers };
      });
    } else {
      // Update a top-level field
      setFormData(prev => ({ ...prev, [field]: value }));
    }
  };

  const nextStep = () => {
    setCurrentStep(prev => prev + 1);
  };

  const prevStep = () => {
    setCurrentStep(prev => prev - 1);
  };

  const goToStep = (step) => {
    setCurrentStep(step);
  };

  // Helper function to determine which component to render
  const renderCurrentStep = () => {
    if (!isInviteCodeValid) {
      // Render Overview in blurred state if code not valid
      return <Overview />; 
    }

    if (inviteCode === 'TEST2025') {
      // Render TestPage for the specific test code
      return (
        <TestPage
          formData={formData}
          setFormData={setFormData}
          // Pass attachments state if TestPage needs it, otherwise remove
          attachments={attachments} 
          setAttachments={setAttachments} 
        />
      );
    }

    // Logic for valid, non-test invite codes
    if (currentStep === 0) {
      return (
        <Overview 
          nextStep={nextStep} 
          formData={formData}
          updateFormData={updateFormData}
        />
      );
    } else if (currentStep > 0 && currentStep <= questions.length) {
      return (
        <QuestionForm
          key={`question-${currentStep}`} // Key is important
          question={questions[currentStep - 1]}
          questionIndex={currentStep - 1}
          totalQuestions={questions.length}
          formData={formData}
          updateFormData={updateFormData}
          nextStep={nextStep}
          prevStep={prevStep}
        />
      );
    } else if (currentStep === questions.length + 1) {
      return (
        <Review
          formData={formData}
          questions={questions}
          goToStep={goToStep}
          inviteCode={inviteCode}
        />
      );
    } else {
      // Fallback or handle unexpected step number, maybe redirect or show error
      // For now, rendering Overview might be safest default
      return <Overview />; 
    }
  };

  return (
    <Router>
      <div className="app-container">
        {!isInviteCodeValid && (
          <div className="invite-code-container">
            <InviteCodeInput onSubmit={handleInviteCodeSubmit} />
          </div>
        )}
        <div className={`content-container ${isInviteCodeValid ? 'clear' : 'blurred'}`}>
          <header className="header">
            <h1>Technology Due Diligence</h1>
          </header>
          <main>
            <Routes>
              {/* Use the helper function to render the element for the main route */}
              <Route path="/" element={renderCurrentStep()} />
              <Route path="/thank-you" element={<ThankYou />} />
            </Routes>
          </main>
        </div>
      </div>
    </Router>
  );
}

export default App;
